import { Injectable, inject } from '@angular/core';
import { LookupPlusService } from 'app/cem/project/services/lookups/lookup-plus.service';
import { AssetTypeIds } from 'app/shared/data/project-enums';
import { Table } from 'app/shared/models';
import { NameValueInt } from 'app/shared/models/name-value';
import { ProjectTypeAssetTypeMatrix } from 'app/shared/models/project-type-asset-type-matrix';

@Injectable({ providedIn: 'root'})
export class ProjectCreateUrls {
  private _lookupPlusService = inject(LookupPlusService);

  public businessAssociateTable: string = 'BusinessAssociates';

  public teamRoleTable: string = 'TeamRoles';
  public teamRoleTableQ: string = '$filter=IsPrimaryRole%20eq%20true';

  public tWell: string = 'Wells';
  public scenarioWell: string = 'ScenarioWells';
  public expand: string = '?$expand=Asset(%24expand%3DCostCenter)';

  public selectWell: string = '&$select=DrillDesignId,Well&$expand=Well($select=Asset,Name,WellId,Ssn,AssetId,DrillDesignId;$expand=Asset($select=AssetName,DevelopmentAreaId,TypeCurveAreaId))';
  public selectAssets: string = '&$select=AssetName,AssetId';
  public filterWell: string;
  public urlWell: string;
  public tWellType: string = 'WellTypes';

  public tProjectTeam: string = 'DefaultProjectTeams';
  public TypeCurveAreaFilter: string = '$filter=AreaTypeId eq 10';
  public tableConstructionType: string = 'ConstructionTypes';
  private projectTypeMatrix: ProjectTypeAssetTypeMatrix[] = [];

  private _Map: Map<number, number[]> = new Map();

  constructor() {
    this.getProjectTypeToAssetTypeMapping(this.projectTypeMatrix);
  }

  public getProjectTypeToAssetTypeMapping(data: ProjectTypeAssetTypeMatrix[]): Promise<ProjectTypeAssetTypeMatrix[]> {
    if (data.length) {
      return Promise.resolve(data);
    }

    let table: Table = Table.ProjectTypeAssetTypeMatrix;

    let result = this._lookupPlusService.getTypedLookup<ProjectTypeAssetTypeMatrix>(Table[table]).then(r => {
      let payload = r.data;
      let cnt = r.total;
      if (!payload || !payload.length) return [];
      for (let i = 0; i < payload.length; i++) {
        let p = payload[i];
        if (p) {
          data.push(p);
          let assetTypes: number[];
          if (this._Map.has(p.ProjectTypeId)) {
            assetTypes = this._Map.get(p.ProjectTypeId);
          } else {
            assetTypes = [];
            this._Map.set(p.ProjectTypeId, assetTypes);
          }
          assetTypes.push(p.AssetTypeId);
        }
      }
      return data;
    }
    );
    return result;
  }

  private replaceAll(source, find, replace) {
    return source.replace(new RegExp(find, 'g'), replace);
  }

  public assetTypeSelector(projectTypeId: number): { availableAssetTypes: NameValueInt[], showAssetSelector: boolean } {
    let result: { availableAssetTypes: NameValueInt[], showAssetSelector: boolean } = {
      availableAssetTypes: [],
      showAssetSelector: false
    };

    if (this._Map.has(projectTypeId)) {
      let assetTypeIds = this._Map.get(projectTypeId);
      if (assetTypeIds.length && assetTypeIds.length > 1 && assetTypeIds.includes(AssetTypeIds.Oil_and_Gas_Well)) {
        //only has oil and gas well
        result.showAssetSelector = true;
      } else {
        result.showAssetSelector = false;
      }

      for (let i = 0; i < assetTypeIds.length; i++) {
        let id = assetTypeIds[i];
        let name = this.replaceAll(AssetTypeIds[id], '_', ' ');
        result.availableAssetTypes.push({ name: name, value: id });
      }
    }
    return result;
  }

  public scheduledWellFilterUrl(name: string, projectTypeId: number, assetTeamId: number, scenarioId: number): string {
    let urlAssetWell: string = '';
    let qAssetName: string = '';
    let filters: string[] = [];
    let filterAssetWell: string = '';
    let urlBaseNoFilter = `ScheduledWellProjectsView?$skip=0&$top=40&$orderby=AssetName`;
    if (name !== '') {
      qAssetName = `contains(AssetName,%20%27${name}%27)%20and%20`;
      filters.push(`contains(AssetName,%20%27${name}%27)`);
    }
    if (projectTypeId) {
      filters.push(`not%20contains(ProjectTypes,%20%27[${projectTypeId}]%27)`);
    }
    if (assetTeamId) {
      filters.push(`AssetTeamId%20eq%20${assetTeamId}`);
    }
    if (scenarioId) {
      filters.push(`ScenarioId%20eq%20${scenarioId}`);
    }
    if (filters.length) {
      filterAssetWell = '&$filter=' + filters.join('%20and%20');
    } else {
      filterAssetWell = '';
    }
    urlAssetWell = `${urlBaseNoFilter}${filterAssetWell}`;
    return urlAssetWell;
  }

  public assetWellFilterUrl(name: string, projectTypeId: number, assetTypeId: number, scenarioId: number, assetTeamId: number, wellProfileId: number): string {
    let urlAssetWell: string = '';
    let qAssetName: string = '';
    let filters: string[] = [];
    let filterAssetWell: string = '';
    let urlBaseNoFilter = `WellsProjectAssetMappingView?$skip=0&$top=40&$orderby=AssetName`;
    if (name !== '') {
      qAssetName = `contains(AssetName,%20%27${name}%27)%20and%20`;
      filters.push(`contains(AssetName,%20%27${name}%27)`);
    }
    if (projectTypeId) {
      filters.push(`not%20contains(ProjectTypes,%20%27[${projectTypeId}]%27)`);
    }
    if (assetTypeId) {
      filters.push(`AssetTypeId%20eq%20${assetTypeId}`);
    }
    if (scenarioId) {
      filters.push(`ScenarioId%20eq%20${scenarioId}`);
    }
    if (assetTeamId) {
      filters.push(`AssetTeamId%20eq%20${assetTeamId}`);
    }
    if (wellProfileId) {
      filters.push(`WellProfileId%20eq%20${wellProfileId}`);
    }
    if (filters.length) {
      filterAssetWell = '&$filter=' + filters.join('%20and%20');
    } else {
      filterAssetWell = '';
    }
    urlAssetWell = `${urlBaseNoFilter}${filterAssetWell}`;
    return urlAssetWell;
  }

  public wellFilterUrl(name: string, projectTypeId: number, assetTypeId: number, scenarioId: number,
    assetTeamId: number, wellProfileId: number, //wellTypeId: number,
    targetFormationId: number, typeCurveAreaId: number,
    top: number = 50): string {
    let filters: string[] = [];

    let assetTypes = this.assetTypeSelector(projectTypeId).availableAssetTypes;

    if (assetTypes.length === 1 && assetTypes[0].value === AssetTypeIds.Oil_and_Gas_Well) {
      assetTypeId = AssetTypeIds.Oil_and_Gas_Well;
    }
    //must use the assetTypeId passed in ONLY, otherwise, you can use all the assetTypes
    if (assetTypeId === AssetTypeIds.Oil_and_Gas_Well) {
      if (name) {
        filters.push(`contains(Well/Asset/AssetName, '${name}')`);
      }
      if (assetTeamId && !scenarioId) {
        filters.push(`Well/AssetTeamId eq ${assetTeamId}`);
      }
      if (scenarioId) {
        filters.push(`ScenarioId eq ${scenarioId}`);
      }
      if (wellProfileId) {
        filters.push(`Well/WellProfileId eq ${wellProfileId}`);
      }
      if (typeCurveAreaId) {
        filters.push(`Well/Asset/TypeCurveAreaId eq ${typeCurveAreaId}`);
      }
      if (targetFormationId) {
        filters.push(`Well/TargetFormationId eq ${targetFormationId}`);
      }
      if (filters.length) {
        this.filterWell = '&$filter=' + filters.join(' and ');
      } else {
        this.filterWell = '';
      }
      this.urlWell = `${this.scenarioWell}?$top=${top}${this.filterWell}${this.selectWell}`;
    } else {

      if (name) {
        filters.push(`contains(AssetName, '${name}')`);
      }

      let sel = this.assetTypeSelector(projectTypeId);
      if (sel.availableAssetTypes.length === 1) {
        filters.push(`AssetTypeId eq ${sel.availableAssetTypes[0].value}`);
      }
      if (assetTeamId) {
        filters.push(`AssetTeamId eq ${assetTeamId}`);
      }

      if (filters.length) {
        let filterWell = filters.join(' and ');
        if (sel.availableAssetTypes.length > 1) {
          let atFilters: string[] = [];
          for (let i = 0; i < sel.availableAssetTypes.length; i++) {
            atFilters.push(`(${filterWell} and AssetTypeId eq ${sel.availableAssetTypes[i].value})`);
          }
          filterWell = '&$filter=' + atFilters.join(' or ');
        }
        else {
          filterWell = '&$filter=' + filterWell;
        }
        this.filterWell = filterWell;
      } else {
        this.filterWell = '';
      }

      this.urlWell = `Assets?$top=${top}${this.filterWell}${this.selectAssets}`;
    }

    return this.urlWell;
  }
}